/**
 * Styles shared by the Address Aliases section (StargardAliasesByFile) and its
 * rule rows (StargardSeenRuleRow). Sources, per the frontend-standards
 * canonicals: panel chrome from StargardMappingPanel.tsx (itself
 * DealerPlatesView ~500-519); the collapsible file header from
 * FileRequirementsPanel.tsx ~1080-1116; small bordered buttons from
 * photoConfigShared smallButtonStyle; the navy add button from
 * StargardMappingPanel addButtonStyle; caption column titles from
 * THEME.typography.caption.
 */
import React from "react"

import { THEME } from "../../config/theme"

export const panelStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  padding: THEME.spacing.md,
  marginBottom: THEME.spacing.lg,
}

export const panelTitleStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 500,
  color: THEME.colors.neutral.light.textPrimary,
  marginBottom: THEME.spacing.xs,
}

export const panelSubtitleStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: 0,
  marginBottom: THEME.spacing.md,
  lineHeight: 1.4,
}

export const fileGroupStyle: React.CSSProperties = {
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  marginBottom: THEME.spacing.sm,
  overflow: "hidden",
}

export const fileHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: THEME.spacing.sm,
  width: "100%",
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  background: "transparent",
  border: "none",
  cursor: "pointer",
  userSelect: "none",
  textAlign: "left",
  fontFamily: THEME.fonts.primary,
  fontSize: 13,
  fontWeight: 600,
  color: THEME.colors.neutral.light.textPrimary,
}

export const fileHeaderSummaryStyle: React.CSSProperties = {
  fontWeight: 400,
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
}

export const noRuleSummaryStyle: React.CSSProperties = {
  ...fileHeaderSummaryStyle,
  color: THEME.colors.semantic.warning,
}

export const fileBodyStyle: React.CSSProperties = {
  padding: `0 ${THEME.spacing.md} ${THEME.spacing.md}`,
}

export const captionStyle: React.CSSProperties = {
  fontSize: THEME.typography.caption.size,
  fontWeight: THEME.typography.caption.weight,
  letterSpacing: THEME.typography.caption.tracking,
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

// Rules table: STARGARD SAYS | BECOMES LOT | CARS | RULE COVERS | action
export const ruleGridStyle = (isMobile: boolean): React.CSSProperties => ({
  display: "grid",
  gridTemplateColumns: isMobile
    ? "minmax(0, 1fr) auto"
    : "minmax(0, 1.4fr) minmax(0, 1fr) 56px 120px 64px",
  gap: THEME.spacing.sm,
  alignItems: "center",
  padding: `${THEME.spacing.xs} 0`,
  borderTop: `1px solid ${THEME.colors.silver}`,
})

// "No rule yet" rows: STARGARD SAYS | lot picker | CARS | add button
export const noRuleGridStyle = (isMobile: boolean): React.CSSProperties => ({
  display: "grid",
  gridTemplateColumns: isMobile ? "minmax(0, 1fr) auto" : "minmax(0, 1.4fr) minmax(0, 1fr) 56px 184px",
  gap: THEME.spacing.sm,
  alignItems: "center",
  padding: `${THEME.spacing.xs} 0`,
  borderTop: `1px solid ${THEME.colors.silver}`,
})

// The clearly separated "No rule yet" group under a file's rules.
export const noRuleGroupStyle: React.CSSProperties = {
  marginTop: THEME.spacing.md,
  paddingTop: THEME.spacing.sm,
  borderTop: `2px solid ${THEME.colors.semantic.warning}`,
}

export const noRuleTitleStyle: React.CSSProperties = {
  ...captionStyle,
  color: THEME.colors.semantic.warning,
}

export const hintTextStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
  lineHeight: 1.4,
  margin: `${THEME.spacing.xs} 0`,
}

// Raw Stargard text is long — mono, wrap-anywhere so phones never overflow.
export const rawTextStyle: React.CSSProperties = {
  fontFamily: THEME.fonts.mono,
  fontSize: 12,
  overflowWrap: "anywhere",
  color: THEME.colors.neutral.light.textPrimary,
}

export const cellTextStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textPrimary,
}

export const countStyle: React.CSSProperties = {
  fontSize: 12,
  whiteSpace: "nowrap",
  color: THEME.colors.neutral.light.textSecondary,
  textAlign: "right",
}

export const inputStyle: React.CSSProperties = {
  boxSizing: "border-box",
  padding: `6px ${THEME.spacing.sm}`,
  fontSize: 13,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.sm,
  fontFamily: THEME.fonts.primary,
  background: THEME.colors.white,
  minWidth: 0,
}

export const monoInputStyle: React.CSSProperties = {
  ...inputStyle,
  fontFamily: THEME.fonts.mono,
  fontSize: 12,
}

export const smallButtonStyle = (disabled = false): React.CSSProperties => ({
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  fontSize: 12,
  fontWeight: 500,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.sm,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.5 : 1,
  fontFamily: THEME.fonts.primary,
  whiteSpace: "nowrap",
})

export const addButtonStyle = (disabled: boolean): React.CSSProperties => ({
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  fontSize: 12,
  fontWeight: 500,
  background: THEME.colors.navy,
  color: THEME.colors.white,
  border: "none",
  borderRadius: THEME.borderRadius.sm,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.5 : 1,
  fontFamily: THEME.fonts.primary,
  whiteSpace: "nowrap",
})

export const deleteLinkStyle: React.CSSProperties = {
  padding: 0,
  fontSize: 12,
  fontWeight: 500,
  background: "transparent",
  color: THEME.colors.semantic.danger,
  border: "none",
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
  textDecoration: "underline",
}

export const editorStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.sm,
  padding: THEME.spacing.sm,
  margin: `${THEME.spacing.xs} 0 ${THEME.spacing.sm}`,
  background: THEME.colors.lightGray,
  borderRadius: THEME.borderRadius.sm,
}

export const radioLabelStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: THEME.spacing.xs,
  fontSize: 13,
  color: THEME.colors.neutral.light.textPrimary,
  cursor: "pointer",
}
